import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import { CHART_COLORS, CHART_THEME } from './constants';
import StatCard, { STAT_ICONS } from '../../components/ui/StatCard';
import PanelCard from '../../components/ui/PanelCard';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar,
} from 'recharts';

export default function DashboardStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/stats/dashboard')
      .then(res => setStats(res.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-12 flex justify-center"><LoadingSpinner label="Loading stats…" /></div>;
  if (!stats) return null;

  const revenueChartData = stats.revenueByDay.map(d => ({
    day: d.day?.slice(5, 10),
    revenue: d.revenue,
    activations: d.activations,
  }));

  return (
    <div className="space-y-6 mb-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Users"     value={stats.totalUsers}                   icon={STAT_ICONS.users} />
        <StatCard label="Active Subs"     value={stats.activeSubs}                   icon={STAT_ICONS.check}   tone="success" />
        <StatCard label="Today's Signups" value={stats.todayRegistrations}           icon={STAT_ICONS.sparkle} tone="brand" />
        <StatCard label="Total Revenue"   value={`€${stats.totalRevenue.toFixed(2)}`} icon={STAT_ICONS.money}   tone="brand" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Frozen Subs"    value={stats.frozenSubs}                       icon={STAT_ICONS.snow}  tone="info" />
        <StatCard label="Banned Users"   value={stats.bannedUsers}                      icon={STAT_ICONS.ban}   tone="danger" />
        <StatCard label="Keys Generated" value={stats.totalKeys}                        icon={STAT_ICONS.key} />
        <StatCard label="Keys Used"      value={`${stats.usedKeys}/${stats.totalKeys}`} icon={STAT_ICONS.chart} />
      </div>

      <PanelCard title="Revenue — Last 14 Days" subtitle="Based on activated license keys.">
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={revenueChartData} margin={{ top: 6, right: 6, left: -12, bottom: 0 }}>
            <defs>
              <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={CHART_COLORS[0]} stopOpacity={0.45} />
                <stop offset="100%" stopColor={CHART_COLORS[0]} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={CHART_THEME.grid} vertical={false} />
            <XAxis dataKey="day" stroke={CHART_THEME.axis} fontSize={11} tickLine={false} axisLine={false} />
            <YAxis stroke={CHART_THEME.axis} fontSize={11} tickLine={false} axisLine={false}
              tickFormatter={v => `€${v}`} />
            <Tooltip
              cursor={{ stroke: CHART_COLORS[0], strokeOpacity: 0.3 }}
              contentStyle={CHART_THEME.tooltip}
              labelStyle={CHART_THEME.tooltipLabel}
              formatter={(value, name) => [
                name === 'revenue' ? `€${value}` : value,
                name === 'revenue' ? 'Revenue' : 'Activations',
              ]}
            />
            <Area type="monotone" dataKey="revenue" stroke={CHART_COLORS[0]} strokeWidth={2}
                  fill="url(#revenueGradient)" />
          </AreaChart>
        </ResponsiveContainer>
      </PanelCard>

      <div className="grid md:grid-cols-2 gap-6 items-start">
        <PanelCard title="Signups — Last 14 Days" subtitle="New user registrations per day.">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart
              data={stats.registrationsByDay.map(d => ({ day: d.day?.slice(5, 10), count: d.count }))}
              margin={{ top: 6, right: 6, left: -18, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_THEME.grid} vertical={false} />
              <XAxis dataKey="day" stroke={CHART_THEME.axis} fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke={CHART_THEME.axis} fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip
                cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                contentStyle={CHART_THEME.tooltip}
                labelStyle={CHART_THEME.tooltipLabel}
              />
              <Bar dataKey="count" name="Signups" fill={CHART_COLORS[2]} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </PanelCard>

        <PanelCard title="Top Plans" subtitle="Most activated subscription plans.">
          {stats.topPlans.length === 0 ? (
            <p className="text-center text-gray-500 text-sm py-8">No activations yet.</p>
          ) : (
            <div className="space-y-1">
              {stats.topPlans.map((plan, i) => {
                const color = CHART_COLORS[i % CHART_COLORS.length];
                return (
                  <div key={plan.label}
                       className="flex items-center justify-between gap-3 py-2.5
                                  border-b border-white/[0.05] last:border-0">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-6 h-6 rounded-lg flex items-center justify-center
                                       text-[11px] font-bold flex-shrink-0"
                        style={{ backgroundColor: `${color}22`, color, border: `1px solid ${color}44` }}>
                        {i + 1}
                      </span>
                      <span className="text-sm text-gray-300 truncate">{plan.label}</span>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-medium text-white tabular-nums">{plan.count} sold</p>
                      <p className="text-xs text-gray-600 tabular-nums">€{plan.revenue?.toFixed(2)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </PanelCard>
      </div>
    </div>
  );
}
